import * as React from 'react';
import CurrentUser from 'models/currentuser';
import AppError from 'utils/appError';
import { CopilotBridge } from 'models/copilot/copilotBridge';
import Task from 'models/tasks/task';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { globalDesktopHeight, globalDesktopSize } from 'globalConstants';

export interface IAppContext {
  useDarkMode: boolean;
  setUseDarkMode: Function;
  isAppLoading: boolean;
  isContentLoading: boolean;
  showContentLoader: Function;
  hideContentLoader: Function;
  error?: AppError;
  showNotification: Function;
  hasScopes: Function;
  setError: Function;
  getAccessToken: Function;
  isAuthenticated: boolean;
  isAuthInProgress: boolean;
  user: CurrentUser;
  task: Task | undefined;
  itemId: string | undefined;
  itemStart: Date | undefined;
  isGettingItemId: boolean;
  isGettingItemStart: boolean;
  isReady: Function;
  globalDataCache: GlobalDataCache;
  firstRun: boolean;
  cacheMiss: Function;
  getGraphInterface: Function;
  isMobileView: boolean;
  windowSize: number;
  windowHeight: number;
  isOnboardingInProgress: boolean | undefined;
  setHelpPanel: (isOpen: boolean) => void;
  hasResourcePanel: boolean;
  showResourcePanel: boolean;
  isMainNavCollapsed: boolean;
  login: () => void;
  logout: () => void;
  switchOrgUnit: (orgUnitId: string) => Promise<void>;
  setState: Function;
  globalFilter: string[];
  setGlobalFilter: (filter: string[]) => void;
  isSelectedScopeEnabled: (clientId: string) => boolean;
  adminConsentScope: (clientId: string, scopes: string[], redirectUrl: string, stateSuffix?: string) => void;
  copilotBridge: CopilotBridge;
}

const appContext: IAppContext = {
  useDarkMode: false,
  setUseDarkMode: () => {},
  isAppLoading: false,
  isContentLoading: false,
  showContentLoader: () => {},
  hideContentLoader: () => {},
  error: undefined,
  showNotification: () => {},
  hasScopes: () => {},
  setError: () => {},
  getAccessToken: () => {},
  isAuthenticated: false,
  isAuthInProgress: false,
  user: {} as CurrentUser,
  task: undefined,
  itemId: undefined,
  itemStart: undefined,
  isGettingItemId: false,
  isGettingItemStart: false,
  isReady: () => {},
  globalDataCache: {} as GlobalDataCache,
  firstRun: false,
  cacheMiss: () => {},
  getGraphInterface: () => {},
  windowSize: globalDesktopSize,
  isMobileView: false,
  windowHeight: globalDesktopHeight,
  isOnboardingInProgress: undefined,
  setHelpPanel: () => {},
  hasResourcePanel: false,
  showResourcePanel: false,
  isMainNavCollapsed: false,
  login: () => {},
  logout: () => {},
  switchOrgUnit: async () => {},
  setState: () => {},
  globalFilter: [],
  setGlobalFilter: () => {},
  isSelectedScopeEnabled: () => false,
  adminConsentScope: async () => {},
  copilotBridge: new CopilotBridge(),
};

const AppContext = React.createContext(appContext);
AppContext.displayName = 'Global app context';

export default AppContext;
